import React, { useState } from "react";
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Menu,
  MenuItem,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Box,
  useMediaQuery,
  Collapse,
  Button,
  ListSubheader,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import { Link } from "react-router-dom";
import logo from "../images/small-logos/websiteLogo.svg";

const ResponsiveNavbar = ({ menuItems, user, handleLogout }) => {
  // Desktop dropdown state
  const [menuState, setMenuState] = useState({
    anchorEl: null,
    activeLabel: "",
  });
  const openDropdown = Boolean(menuState.anchorEl);

  const handleDropdownOpen = (event, label) => {
    setMenuState({ anchorEl: event.currentTarget, activeLabel: label });
  };

  const handleDropdownClose = () => {
    setMenuState({ anchorEl: null, activeLabel: "" });
  };

  // Mobile Drawer state
  const [drawerOpen, setDrawerOpen] = useState(false);
  const toggleDrawer = (open) => () => setDrawerOpen(open);

  // Mobile submenu collapse state for each parent label
  const [mobileSubmenuOpen, setMobileSubmenuOpen] = useState({});
  const toggleMobileSubmenu = (label) =>
    setMobileSubmenuOpen((prev) => ({ ...prev, [label]: !prev[label] }));

  // Determine if we are on desktop using MUI's breakpoint hook
  const isDesktop = useMediaQuery((theme) => theme.breakpoints.up("md"));

  return (
    <>
      <AppBar position="fixed" sx={{ backgroundColor: "#000d1a" }}>
        <Toolbar>
          {/* Mobile hamburger button */}
          {!isDesktop && (
            <IconButton
              edge="start"
              color="inherit"
              onClick={toggleDrawer(true)}
            >
              <MenuIcon />
            </IconButton>
          )}
          <Typography
            variant="h6"
            component={Link}
            to="/"
            sx={{
              display: "flex",
              alignItems: "center",
              color: "inherit",
              textDecoration: "none",
              flexGrow: 1,
            }}
          >
            <img
              src={logo}
              alt="Website Logo"
              style={{
                height: "40px",
                marginRight: "8px",
                filter: "invert(1)",
              }}
            />
            Alexia Coaching
          </Typography>

          {/* Desktop Menu */}
          {isDesktop && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              {menuItems.map((item) => (
                <Box
                  key={item.label}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    position: "relative",
                  }}
                >
                  {/* Conditionally render the label */}
                  {item.children ? (
                    <Typography
                      onClick={(e) => {
                        e.preventDefault();
                        handleDropdownOpen(e, item.label);
                      }}
                      sx={{
                        color: "inherit",
                        textDecoration: "none",
                        cursor: "pointer",
                      }}
                    >
                      {item.label}
                    </Typography>
                  ) : (
                    <Typography
                      component={Link}
                      to={item.href}
                      sx={{
                        color: "inherit",
                        textDecoration: "none",
                        cursor: "pointer",
                      }}
                    >
                      {item.label}
                    </Typography>
                  )}
                  {/* If there are children, add an IconButton to toggle dropdown */}
                  {item.children && (
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation(); // Prevent triggering the label's onClick
                        handleDropdownOpen(e, item.label);
                      }}
                      size="small"
                      sx={{ color: "inherit" }}
                    >
                      <ExpandMore fontSize="inherit" />
                    </IconButton>
                  )}
                  {/* Dropdown Menu for desktop */}
                  {item.children && menuState.activeLabel === item.label && (
                    <Menu
                      anchorEl={menuState.anchorEl}
                      open={openDropdown}
                      onClose={handleDropdownClose}
                      MenuListProps={{
                        dense: true,
                        onMouseLeave: handleDropdownClose,
                      }}
                    >
                      {item.children.map((child) => (
                        <Box key={child.heading}>
                          <ListSubheader
                            dense
                            component="div"
                            sx={{
                              fontWeight: "bold",
                              color: "text.secondary",
                              backgroundColor: "transparent",
                              py: 0.5,
                              px: 1,
                            }}
                          >
                            {child.heading}
                          </ListSubheader>
                          {child.submenu.map((sub) => (
                            <MenuItem
                              key={sub.label}
                              component={Link}
                              to={sub.href}
                              onClick={handleDropdownClose}
                            >
                              {sub.label}
                            </MenuItem>
                          ))}
                        </Box>
                      ))}
                    </Menu>
                  )}
                </Box>
              ))}
              {/* Desktop login/logout button */}
              {user ? (
                <Button color="inherit" onClick={handleLogout}>
                  Logout
                </Button>
              ) : (
                <Button color="inherit" component={Link} to="/login">
                  Login
                </Button>
              )}
            </Box>
          )}

          {/* For mobile, include the login/logout button in the AppBar */}
          {!isDesktop &&
            (user ? (
              <Button color="inherit" onClick={handleLogout}>
                Logout
              </Button>
            ) : (
              <Button color="inherit" component={Link} to="/login">
                Login
              </Button>
            ))}
        </Toolbar>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer anchor="left" open={drawerOpen} onClose={toggleDrawer(false)}>
        <Box sx={{ width: 250 }} role="presentation">
          <List dense>
            {menuItems.map((item) => (
              <React.Fragment key={item.label}>
                <ListItem
                  button
                  // If item has children, clicking the text should not navigate immediately
                  component={Link}
                  to={item.href}
                  onClick={
                    item.children
                      ? (e) => {
                          e.preventDefault();
                          toggleMobileSubmenu(item.label);
                        }
                      : toggleDrawer(false)
                  }
                >
                  <ListItemText primary={item.label} />
                  {item.children && (
                    <IconButton
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation(); // Prevent the event from bubbling to the ListItem
                        toggleMobileSubmenu(item.label);
                      }}
                      edge="end"
                    >
                      {mobileSubmenuOpen[item.label] ? (
                        <ExpandLess />
                      ) : (
                        <ExpandMore />
                      )}
                    </IconButton>
                  )}
                </ListItem>
                {item.children && (
                  <Collapse
                    in={mobileSubmenuOpen[item.label]}
                    timeout="auto"
                    unmountOnExit
                  >
                    <List component="div" disablePadding dense>
                      {item.children.map((child) => (
                        <React.Fragment key={child.heading}>
                          <ListSubheader
                            dense
                            component="div"
                            sx={{
                              color: "text.secondary", // muted color compared to links
                              fontStyle: "italic", // italic text
                              textDecoration: "underline", // underline effect
                              cursor: "default", // no pointer effect
                              fontWeight: 500,
                              fontSize: "0.7rem", // slightly smaller than link text
                              //px: 1, // some horizontal padding
                              py: 0,
                              mb: 0, // remove bottom margin
                              lineHeight: 1.2, // adjust line height if needed
                              borderBottom: "2px solid transparent",
                              transition: "border-bottom 0.2s",
                              "&:hover": {
                                borderBottom: "2px solid transparent", // no change on hover
                              },
                            }}
                          >
                            {child.heading}
                          </ListSubheader>
                          {child.submenu.map((sub) => (
                            <ListItem
                              key={sub.label}
                              button
                              component={Link}
                              to={sub.href}
                              sx={{ pl: 6 }}
                              onClick={toggleDrawer(false)}
                            >
                              <ListItemText primary={sub.label} />
                            </ListItem>
                          ))}
                        </React.Fragment>
                      ))}
                    </List>
                  </Collapse>
                )}
              </React.Fragment>
            ))}
            {/* Mobile login/logout in the drawer */}
            <ListItem
              button
              onClick={() => {
                toggleDrawer(false)();
                if (user) {
                  handleLogout();
                }
              }}
            >
              <ListItemText primary={user ? "Logout" : "Login"} />
            </ListItem>
          </List>
        </Box>
      </Drawer>
    </>
  );
};

export default ResponsiveNavbar;
